/**
 * Flatbox-offerterna på ett Flatbox-lead (flatbox_offers där leadId = leadet).
 * Offerterna är alternativ för samma köp – raderna står i samma kolumner så
 * att de går att jämföra. Varje rad är en länk till offerten i konfiguratorn.
 *
 * Renderar ingenting (och läser ingenting) om leadet inte gäller Flatbox eller
 * användaren saknar Flatbox-behörighet.
 */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, Button, Chip, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { canUseFlatbox } from '../../utils/permissions';
import { isFlatboxLead, flatboxOffertRad, sorteraFlatboxOfferter, kampanjForLead, kampanjText, toTimeFlowInput } from './flatboxLead';
import TimeFlowButton from '../../components/TimeFlowButton';

// Upplägg är den enda flexibla kolumnen (kortas med … vid behov); övriga
// krymper till sitt innehåll. Raderna är subgrid i ett gemensamt grid så att
// kolumnerna linjerar fast varje rad är en egen länk. Ingen cell radbryts –
// på smal skärm scrollar tabellen i sidled.
const KOLUMNER = 'auto minmax(120px, 1fr) auto auto auto auto';
const RAD = { gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'subgrid', px: 1 };
const CELL = { whiteSpace: 'nowrap' };
// Månadspris: högerjusterat, 8 px + kolumnmellanrummet 16 px = 24 px mot Datum
const PRIS = { ...CELL, textAlign: 'right', pr: 1, fontVariantNumeric: 'tabular-nums' };
const RUBRIKER = [
  { h: 'Offert' }, { h: 'Upplägg' }, { h: 'Användare' },
  { h: 'Månadspris', sx: PRIS }, { h: 'Datum' }, { h: 'Status' },
];
const kr = n => (n == null ? '–' : `${Math.round(n).toLocaleString('sv-SE')} kr`);

export default function FlatboxOfferterSektion({ lead, products, userProfile }) {
  const aktiv = !!lead && canUseFlatbox(userProfile) && isFlatboxLead(lead, products);
  const leadId = lead?.id;
  const [offerter, setOfferter] = useState(null);
  const [fel, setFel] = useState('');

  useEffect(() => {
    if (!aktiv) return undefined;
    let aktuell = true;
    getDocs(query(collection(db, 'flatbox_offers'), where('leadId', '==', leadId)))
      .then(s => { if (aktuell) setOfferter(sorteraFlatboxOfferter(s.docs.map(d => d.data()))); })
      .catch(e => { if (aktuell) setFel(`Kunde inte läsa Flatbox-offerter: ${e.message || e}`); });
    return () => { aktuell = false; };
  }, [aktiv, leadId]);

  if (!aktiv) return null;
  const rader = (offerter || []).map(d => ({ ...flatboxOffertRad(d), doc: d }));
  const kampanj = kampanjText(kampanjForLead(offerter));

  return (
    <Paper sx={{ p: 2.5, mb: 2 }} data-testid="flatbox-offerter">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
          Flatbox-offerter{offerter ? ` (${offerter.length})` : ''}
          {kampanj && (
            <Chip size="small" label={kampanj} data-testid="kampanj-etikett"
              sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#E8EAF6', color: '#3949AB' }} />
          )}
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />}
          component={RouterLink} to={`/flatbox?lead=${encodeURIComponent(leadId)}`}
          sx={{ bgcolor: '#E30613', '&:hover': { bgcolor: '#b80510' } }}>
          Ny Flatbox-offert
        </Button>
        <Button size="small" startIcon={<OpenInNewIcon />} component={RouterLink} to="/flatbox">
          Öppna Flatbox-konfiguratorn
        </Button>
      </Box>

      {fel && <Alert severity="error" sx={{ mb: 1 }}>{fel}</Alert>}
      {offerter && !rader.length && (
        <Typography variant="body2" color="text.secondary">Inga Flatbox-offerter kopplade till leadet än.</Typography>
      )}
      {rader.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: KOLUMNER, columnGap: 2, minWidth: 640 }}>
            <Box sx={{ ...RAD, pb: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              {RUBRIKER.map(({ h, sx }) => (
                <Typography key={h} variant="caption" color="text.secondary"
                  sx={{ ...CELL, fontWeight: 600, ...sx }}>{h}</Typography>
              ))}
            </Box>
            {rader.map(r => {
              const upplagg = `${r.upplagg}${r.kampanj ? ' · prova-på' : ''}`;
              return (
                <React.Fragment key={r.offerNo}>
                <Box component={RouterLink} to={`/flatbox?offer=${encodeURIComponent(r.offerNo)}`}
                  title={`Öppna ${r.offerNo} i Flatbox-konfiguratorn`}
                  sx={{ ...RAD, alignItems: 'center', py: 0.75, color: 'inherit', textDecoration: 'none',
                    borderBottom: '1px solid', borderColor: 'divider',
                    bgcolor: r.signerad ? 'rgba(46,125,50,0.08)' : 'transparent',
                    '&:hover': { bgcolor: r.signerad ? 'rgba(46,125,50,0.14)' : 'action.hover' } }}>
                  <Typography variant="body2" sx={{ ...CELL, fontFamily: 'monospace', fontWeight: 700, color: '#E30613' }}>
                    {r.offerNo}
                  </Typography>
                  <Typography variant="body2" noWrap title={upplagg} sx={{ minWidth: 0 }}>{upplagg}</Typography>
                  <Typography variant="body2" sx={CELL}>{r.anvandare}</Typography>
                  <Typography variant="body2" sx={PRIS}>{kr(r.manadspris)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ...CELL, fontVariantNumeric: 'tabular-nums' }}>
                    {r.datum || '–'}
                  </Typography>
                  <Box sx={{ ...CELL, display: 'flex' }}>
                    {r.signerad ? (
                      <Chip size="small" label={`Signerad ${r.signeringsdatum}`}
                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#2E7D32', color: '#fff' }} />
                    ) : r.skickad ? (
                      <Chip size="small" label="Skickad" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                    ) : null}
                  </Box>
                </Box>
                {/* Egen rad under offertraden – en knapp får inte ligga i länken */}
                {r.signerad && (
                  <Box sx={{ gridColumn: '1 / -1', px: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider',
                    bgcolor: 'rgba(46,125,50,0.08)' }} data-testid="timeflow-rad">
                    <TimeFlowButton input={toTimeFlowInput(r.doc)} />
                  </Box>
                )}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      )}
    </Paper>
  );
}
